import * as React from 'react';
import { useState } from 'react';
import { Table2 } from 'lucide-react';
import { useCurrentEditor } from '@tiptap/react';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { ToolbarButton } from './ToolbarButton';
import { kitT, type KitTranslateFn } from '../../../lib/kitTranslate';

const GRID_MAX = 10;

export interface TableGridPickerProps {
    translate?: KitTranslateFn;
}

export const TableGridPicker: React.FC<TableGridPickerProps> = ({ translate }) => {
    const { editor } = useCurrentEditor();
    const [open, setOpen] = useState(false);
    const [hoverRows, setHoverRows] = useState(1);
    const [hoverCols, setHoverCols] = useState(1);

    const tx = (k: string, fb: string) => kitT(translate, k, fb);

    const handleCellHover = (row: number, col: number) => {
        setHoverRows(row + 1);
        setHoverCols(col + 1);
    };

    const handleInsert = () => {
        if (!editor) return;
        editor
            .chain()
            .focus()
            .insertTable({
                rows: hoverRows,
                cols: hoverCols,
                withHeaderRow: true,
            })
            .run();
        setOpen(false);
    };

    const handleOpenChange = (next: boolean) => {
        setOpen(next);
        if (next) {
            setHoverRows(1);
            setHoverCols(1);
        }
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <ToolbarButton
                    isActive={editor?.isActive('table')}
                    aria-label={tx('kRtgAriaInsertTable', 'Insert table')}
                >
                    <Table2 className="h-4 w-4" />
                </ToolbarButton>
            </PopoverTrigger>
            <PopoverContent
                className="rich-text-table-grid-picker w-auto p-3 shadow-md border-0"
                align="start"
                sideOffset={4}
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <div className="rich-text-table-grid-picker-grid grid gap-0.5 p-1">
                    {Array.from({ length: GRID_MAX }, (_, row) => (
                        <div key={row} className="flex gap-0.5">
                            {Array.from({ length: GRID_MAX }, (_, col) => {
                                const inSelection = row < hoverRows && col < hoverCols;
                                const aria = tx('kRtgAriaCellSize', '{rows} by {cols} table')
                                    .replace('{rows}', String(row + 1))
                                    .replace('{cols}', String(col + 1));
                                return (
                                    <button
                                        key={col}
                                        type="button"
                                        aria-label={aria}
                                        className={`rich-text-table-grid-picker-cell h-3 w-3 rounded-sm border border-border transition-colors duration-200 cursor-pointer ${
                                            inSelection
                                                ? 'bg-primary/25 border-primary/50'
                                                : 'bg-muted/40 hover:bg-muted/70'
                                        }`}
                                        onMouseEnter={() => handleCellHover(row, col)}
                                        onClick={handleInsert}
                                    />
                                );
                            })}
                        </div>
                    ))}
                </div>
                <p className="rich-text-table-grid-picker-dim mt-2 text-center text-xs text-muted-foreground tabular-nums">
                    {hoverCols} × {hoverRows}
                </p>
            </PopoverContent>
        </Popover>
    );
};

TableGridPicker.displayName = 'TableGridPicker';
